import { useTranslation } from 'react-i18next'
import { Clock } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { Exercise } from '../../types/exercise'

function formatDuration(minutes: number, mPrefix: string, hPrefix: string): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}${mPrefix}`
  if (m === 0) return `${h}${hPrefix}`
  return `${h}${hPrefix} ${m}${mPrefix}`
}

interface ExerciseSummaryRowProps {
  exercise: Exercise
  className?: string
  /** Slot rendered on the far right (e.g. action button or chevron) */
  right?: React.ReactNode
  compact?: boolean
}

export function ExerciseSummaryRow({ exercise, className, right, compact = false }: ExerciseSummaryRowProps) {
  const { t } = useTranslation()
  const mPrefix = t('common.minutesShort', 'm')
  const hPrefix = t('common.hoursShort', 'h')

  return (
    <div className={cn('flex items-center gap-3 min-w-0', className)}>
      {exercise.image_url ? (
        <img
          src={exercise.image_url}
          alt=""
          className={cn(
            'rounded-md object-cover bg-muted shrink-0',
            compact ? 'size-10' : 'size-12'
          )}
          loading="lazy"
        />
      ) : (
        <div className={cn('rounded-md bg-muted shrink-0', compact ? 'size-10' : 'size-12')} />
      )}

      <div className="flex flex-col min-w-0 flex-1">
        <span className="flex items-center gap-1.5 min-w-0">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">
            {exercise.category.name}
          </span>
          {/* Tells the user's own exercises apart from the shared catalog. */}
          {exercise.owned && (
            <span className="text-[10px] uppercase tracking-wide text-primary shrink-0">
              {t('trainingDiary.customExercise.badge', 'Mine')}
            </span>
          )}
        </span>
        <span className={cn('font-medium truncate', compact ? 'text-sm' : 'text-sm')}>
          {exercise.title}
        </span>
        {exercise.duration_minutes != null && (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="size-3" />
            {formatDuration(exercise.duration_minutes, mPrefix, hPrefix)}
          </span>
        )}
      </div>

      {right && <div className="shrink-0">{right}</div>}
    </div>
  )
}
